/**
 * portal-berechtigungen/mock.ts — mock-data mode for Bereich 6, modelling the BACKEND's
 * committed contract (portal-visibility.service.ts) so dev + tests exercise the real shapes.
 *
 * Two parts:
 *  1. MOCK_IGB2B_KATEGORIEN — the fixed 59-category IGB2B taxonomy, each tagged with its
 *     family-head branchencode (Igb2bKategorieDto.familie).
 *  2. A resettable, per-customer in-memory STORE that mirrors the service:
 *       - the einfach|gruppenbasiert mode toggle,
 *       - default-group SEEDING on the first switch to gruppenbasiert (4 editable templates
 *         + the computed, non-deletable "Nicht zugeordnet" system group),
 *       - GRANULAR group CRUD + category/member assign-remove,
 *       - every write returns the full { mode, groups } snapshot (the wire contract).
 *
 * The store is keyed by `${subjectType}:${subjectId}`. Call `resetPortalBerechtigungenMock()`
 * in a test `beforeEach`.
 *
 * [Source: apps/api/src/modules/customer-portal/portal-visibility.service.ts] (state machine)
 * [Source: packages/shared/src/portal-permission-groups.ts] (templates + families)
 */

import {
  PORTAL_GROUP_SYSTEM_NAME,
  PORTAL_GROUP_SYSTEM_POSITION,
  PORTAL_GROUP_TEMPLATES,
  type Igb2bKategorieDto,
  type PortalGroupDto,
  type PortalGroupMemberDto,
  type PortalGroupsResponseDto,
  type PortalVisibilityMode,
  type PortalVisibilityModeDto,
} from '@brokly/shared';
import { familieForBranchencode, type PortalMemberOption } from './types';

// ── The IGB2B taxonomy (59 categories, 9 Branchenfamilien) ───────────────────
const RAW: readonly [string, string, string][] = [
  ['10000', 'VEHICLE_INSURANCE', 'Fahrzeugversicherung'],
  ['11000', 'MOTOR_VEHICLE_INSURANCE', 'Motorfahrzeugversicherung'],
  ['12000', 'AIRCRAFT_INSURANCE', 'Luftfahrtzeugversicherung'],
  ['13000', 'WATERCRAFT_INSURANCE', 'Wasserfahrzeugversicherung'],
  ['14000', 'MOTORCYCLE_INSURANCE', 'Motorradversicherung'],
  ['20000', 'LIABILITY_INSURANCE', 'Haftpflichtversicherungen'],
  ['21000', 'BUSINESS_LIABILITY_INSURANCE', 'Betriebs- und Berufshaftpflicht Versicherung'],
  ['22000', 'BUILDING_LIABILITY_INSURANCE', 'Gebäudehaftpflicht Versicherung'],
  ['23000', 'FINANCIAL_LOSS_LIABILITY_INSURANCE', 'Vermögensschadenhaftpflicht Versicherung'],
  ['24000', 'OFFICERS_LIABILITY_INSURANCE', 'Organhaftpflicht Versicherung'],
  ['25000', 'BUILDERS_LIABILITY_INSURANCE', 'Bauherrenhaftpflicht Versicherung'],
  ['26000', 'PERSONAL_LIABILITY_INSURANCE', 'Privathaftpflicht Versicherung'],
  ['29900', 'OTHER_LIABILITY_INSURANCE', 'Übrige Haftpflichtversicherungen'],
  ['30000', 'ACCIDENT_INSURANCE', 'Unfallversicherungen'],
  ['31000', 'SINGLE_ACCIDENT_INSURANCE', 'Einzelunfall Versicherung'],
  ['33000', 'GROUP_ACCIDENT_INSURANCE', 'Kollektivunfall Versicherung'],
  ['34000', 'AGRICULTURAL_ACCIDENT_INSURANCE', 'Landwirtschaftliche Unfallversicherung'],
  ['35000', 'SCHOOL_ACCIDENT_INSURANCE', 'Schülerunfallversicherung'],
  ['36000', 'SUPPLEMENTARY_ACCIDENT_INSURANCE', 'Unfallzusatzversicherung (UVGZ)'],
  ['38000', 'COMPULSORY_ACCIDENT_INSURANCE', 'Obligatorische Unfallversicherung (UVG/OUFL)'],
  ['39900', 'OTHER_ACCIDENT_INSURANCE', 'Übrige Unfallversicherungen'],
  ['40000', 'HEALTH_INSURANCE', 'Krankenversicherung'],
  ['41000', 'INDIVIDUAL_HEALTH_INSURANCE', 'Krankenversicherung Einzel'],
  ['42000', 'COLLECTIVE_SICKNESS_BENEFIT_INSURANCE', 'Kollektiv Krankentaggeldversicherung'],
  ['43000', 'OTHER_HEALTH_INSURANCE', 'Übrige Krankenversicherung'],
  ['50000', 'TRANSPORT_INSURANCE', 'Transportversicherungen'],
  ['51000', 'CARGO_INSURANCE', 'Warenversicherung'],
  ['52000', 'SPECIE_INSURANCE', 'Valorenversicherung'],
  ['53000', 'LUGGAGE_INSURANCE', 'Reisegepäckversicherung'],
  ['54000', 'CARRIER_INSURANCE', 'Frachtführer Versicherung'],
  ['55000', 'FREIGHT_FORWARDER_LIABILITY_INSURANCE', 'Spediteurhaftpflicht Versicherung'],
  ['56000', 'TRANSPORT_COMPREHENSIVE_INSURANCE', 'Transport Kasko Versicherung'],
  ['58000', 'ART_INSURANCE', 'Kunstgegenstände Versicherung'],
  ['59900', 'OTHER_TRANSPORT_INSURANCE', 'Übrige Transportversicherungen'],
  ['60000', 'PROPERTY_INSURANCE', 'Sachversicherungen (Kombinationen)'],
  ['65000', 'VALUABLES_INSURANCE', 'Wertsachenversicherung'],
  ['67000', 'BUSINESS_INTERRUPTION_INSURANCE', 'Betriebsunterbrechungsversicherung'],
  ['69900', 'OTHER_PROPERTY_INSURANCE', 'Übrige Sachversicherungen'],
  ['70000', 'TECHNICAL_INSURANCE', 'Technische Versicherungen'],
  ['71000', 'CONSTRUCTION_INSURANCE', 'Bauwesen Versicherung'],
  ['72000', 'INSTALLATION_INSURANCE', 'Montageversicherung'],
  ['73000', 'MACHINERY_INSURANCE', 'Maschinenversicherung'],
  ['74000', 'COMPUTER_SYSTEMS_INSURANCE', 'EDV-Anlagen'],
  ['75000', 'TECHNICAL_EQUIPMENT_INSURANCE', 'Allg. technische Anlagen-Versicherung (ATA)'],
  ['79900', 'OTHER_TECHNICAL_INSURANCE', 'Übrige technische Versicherungen'],
  ['80000', 'INSURANCE_OTHER_SECTORS', 'Versicherungen übrige Branchen'],
  ['81000', 'CREDIT_INSURANCE', 'Kreditversicherungen'],
  ['82000', 'LEGAL_PROTECTION_INSURANCE', 'Rechtschutzversicherung'],
  ['83500', 'TRAVEL_INSURANCE', 'Assistance und Reiseversicherung'],
  ['85000', 'PANDEMIC_INSURANCE', 'Epidemie-, Hygiene- und Pandemieversicherung'],
  ['87000', 'CYBER_INSURANCE', 'Cyberversicherung'],
  ['88000', 'DEPOSIT_INSURANCE', 'Garantie- und Kautionsversicherung'],
  ['89900', 'OTHER_INSURANCE', 'Übrigen Versicherungen'],
  ['90000', 'LIFE_INSURANCE', 'Lebensversicherungen (Kombinationen)'],
  ['91000', 'INDIVIDUAL_LIFE_INSURANCE', 'Einzellebensversicherung (Kombinationen)'],
  ['92000', 'VESTED_BENEFITS_POLICY', 'Freizügigkeitspolice'],
  ['93000', 'GROUP_LIFE_INSURANCE', 'Kollektivlebensversicherung (Kombinationen)'],
  ['94000', 'MORTGAGES_INSURANCE', 'Hypotheken'],
  ['99900', 'OTHER_LIFE_INSURANCE', 'Übrige Lebensversicherungen und Finanzprodukte'],
];

export const MOCK_IGB2B_KATEGORIEN: Igb2bKategorieDto[] = RAW.map(
  ([branchencode, jsonBezeichnung, deBezeichnung]) => ({
    branchencode,
    jsonBezeichnung,
    deBezeichnung,
    familie: familieForBranchencode(branchencode),
  }),
);

const ALL_CODES = MOCK_IGB2B_KATEGORIEN.map((k) => k.branchencode);

/** The stable id of the auto "Nicht zugeordnet" system group (computed on read). */
export const SYSTEM_GRUPPE_ID = 'grp-system';

// ── Store ────────────────────────────────────────────────────────────────────

/** An editable/custom group (the system group is derived on read, never stored). */
interface EditableGroup {
  id: string;
  name: string;
  istTemplate: boolean;
  templateKey: string | null;
  position: number;
  branchencodes: string[];
  members: PortalGroupMemberDto[];
}

interface CustomerState {
  mode: PortalVisibilityMode;
  groups: EditableGroup[];
  /** True once the system group has been seeded (first switch to gruppenbasiert). */
  seeded: boolean;
  pool: PortalMemberOption[];
}

const STORE = new Map<string, CustomerState>();
let idSeq = 0;

const keyOf = (subjectType: string, subjectId: string): string => `${subjectType}:${subjectId}`;

/** The assignable pool: the primary holder (grantId null) + a few grants. */
function defaultPool(): PortalMemberOption[] {
  return [
    { grantId: null, label: 'Hauptkontakt (Kunde)', email: '', status: 'primary' },
    { grantId: 'grant-anna', label: 'Anna Muster', email: 'anna@kunde.ch', status: 'active' },
    { grantId: 'grant-beat', label: 'Beat Kunz', email: 'beat@kunde.ch', status: 'active' },
    { grantId: 'grant-carla', label: 'Carla Roth', email: 'carla@kunde.ch', status: 'invited' },
  ];
}

/** The 4 editable template groups seeded when a customer switches to gruppenbasiert. */
function seedTemplateGroups(): EditableGroup[] {
  return PORTAL_GROUP_TEMPLATES.map((tpl) => ({
    id: `grp-${tpl.templateKey}`,
    name: tpl.name,
    istTemplate: true,
    templateKey: tpl.templateKey,
    position: tpl.position,
    branchencodes: [...tpl.branchencodes],
    members: [],
  }));
}

function ensureState(subjectType: string, subjectId: string): CustomerState {
  const key = keyOf(subjectType, subjectId);
  let s = STORE.get(key);
  if (!s) {
    s = { mode: 'einfach', groups: [], seeded: false, pool: defaultPool() };
    STORE.set(key, s);
  }
  return s;
}

function toDto(g: EditableGroup): PortalGroupDto {
  return {
    id: g.id,
    name: g.name,
    istSystem: false,
    istTemplate: g.istTemplate,
    templateKey: g.templateKey,
    position: g.position,
    branchencodes: [...g.branchencodes],
    members: g.members.map((m) => ({ ...m })),
  };
}

/**
 * The auto "Nicht zugeordnet" system group, computed on every read:
 *   - branchencodes = complement of all editable groups' categories (unassigned codes),
 *   - members       = ALL portal users (the whole pool).
 */
function computeSystemGroup(state: CustomerState): PortalGroupDto {
  const assigned = new Set(state.groups.flatMap((g) => g.branchencodes));
  const members: PortalGroupMemberDto[] = state.pool.map((p) => ({
    grantId: p.grantId,
    isPrimary: p.grantId === null,
    label: p.grantId === null ? null : p.label,
  }));
  return {
    id: SYSTEM_GRUPPE_ID,
    name: PORTAL_GROUP_SYSTEM_NAME,
    istSystem: true,
    istTemplate: false,
    templateKey: null,
    position: PORTAL_GROUP_SYSTEM_POSITION,
    branchencodes: ALL_CODES.filter((c) => !assigned.has(c)),
    members,
  };
}

/** Assemble the { mode, groups } snapshot (system group last by position; ORDER BY position, name). */
function snapshotOf(state: CustomerState): PortalGroupsResponseDto {
  const groups: PortalGroupDto[] = state.groups.map(toDto);
  if (state.seeded) groups.push(computeSystemGroup(state));
  groups.sort((a, b) => a.position - b.position || a.name.localeCompare(b.name));
  return { mode: state.mode, groups };
}

function findEditable(state: CustomerState, groupId: string): EditableGroup {
  const g = state.groups.find((x) => x.id === groupId);
  if (!g) throw new Error('Gruppe nicht gefunden.');
  return g;
}

// ── Mock read/write operations (used by api.ts in mock mode) ─────────────────

export function mockGetMode(subjectType: string, subjectId: string): PortalVisibilityModeDto {
  const s = ensureState(subjectType, subjectId);
  return { mode: s.mode, groupCount: s.groups.length };
}

export function mockGetMemberPool(subjectType: string, subjectId: string): PortalMemberOption[] {
  return ensureState(subjectType, subjectId).pool.map((p) => ({ ...p }));
}

export function mockSetMode(
  subjectType: string,
  subjectId: string,
  mode: PortalVisibilityMode,
): PortalGroupsResponseDto {
  const s = ensureState(subjectType, subjectId);
  s.mode = mode;
  // Seed the 4 templates + the system group the first time a customer switches to group-mode.
  if (mode === 'gruppenbasiert' && !s.seeded) {
    s.groups = [...seedTemplateGroups(), ...s.groups];
    s.seeded = true;
  }
  return snapshotOf(s);
}

export function mockGetGruppen(subjectType: string, subjectId: string): PortalGroupsResponseDto {
  return snapshotOf(ensureState(subjectType, subjectId));
}

export function mockCreateGruppe(
  subjectType: string,
  subjectId: string,
  body: { name: string; branchencodes?: string[] },
): PortalGroupsResponseDto {
  const s = ensureState(subjectType, subjectId);
  const name = body.name.trim();
  if (s.groups.some((g) => g.name.toLowerCase() === name.toLowerCase())) {
    throw new Error('Ein Gruppenname ist pro Kunde eindeutig.');
  }
  s.groups.push({
    id: `grp-custom-${++idSeq}`,
    name,
    istTemplate: false,
    templateKey: null,
    position: 100,
    branchencodes: body.branchencodes ? [...new Set(body.branchencodes)] : [],
    members: [],
  });
  return snapshotOf(s);
}

export function mockRenameGruppe(
  subjectType: string,
  subjectId: string,
  groupId: string,
  name: string,
): PortalGroupsResponseDto {
  const s = ensureState(subjectType, subjectId);
  const g = findEditable(s, groupId);
  const next = name.trim();
  if (s.groups.some((x) => x.id !== groupId && x.name.toLowerCase() === next.toLowerCase())) {
    throw new Error('Ein Gruppenname ist pro Kunde eindeutig.');
  }
  g.name = next;
  return snapshotOf(s);
}

export function mockDeleteGruppe(
  subjectType: string,
  subjectId: string,
  groupId: string,
): PortalGroupsResponseDto {
  const s = ensureState(subjectType, subjectId);
  s.groups = s.groups.filter((x) => x.id !== groupId);
  return snapshotOf(s);
}

export function mockAddKategorie(
  subjectType: string,
  subjectId: string,
  groupId: string,
  branchencode: string,
): PortalGroupsResponseDto {
  const s = ensureState(subjectType, subjectId);
  const g = findEditable(s, groupId);
  if (!g.branchencodes.includes(branchencode)) g.branchencodes.push(branchencode);
  return snapshotOf(s);
}

export function mockRemoveKategorie(
  subjectType: string,
  subjectId: string,
  groupId: string,
  branchencode: string,
): PortalGroupsResponseDto {
  const s = ensureState(subjectType, subjectId);
  const g = findEditable(s, groupId);
  g.branchencodes = g.branchencodes.filter((c) => c !== branchencode);
  return snapshotOf(s);
}

export function mockAddMember(
  subjectType: string,
  subjectId: string,
  groupId: string,
  grantId: string | null,
): PortalGroupsResponseDto {
  const s = ensureState(subjectType, subjectId);
  const g = findEditable(s, groupId);
  if (!g.members.some((m) => m.grantId === grantId)) {
    const opt = s.pool.find((p) => p.grantId === grantId);
    g.members.push({
      grantId,
      isPrimary: grantId === null,
      label: grantId === null ? null : (opt?.label ?? null),
    });
  }
  return snapshotOf(s);
}

export function mockRemoveMember(
  subjectType: string,
  subjectId: string,
  groupId: string,
  grantId: string | null,
): PortalGroupsResponseDto {
  const s = ensureState(subjectType, subjectId);
  const g = findEditable(s, groupId);
  g.members = g.members.filter((m) => m.grantId !== grantId);
  return snapshotOf(s);
}

/** Test helper — put a customer into a known mode (seeds templates on group-mode). */
export function __setMockMode(
  subjectType: string,
  subjectId: string,
  mode: PortalVisibilityMode,
): void {
  mockSetMode(subjectType, subjectId, mode);
}

/** Reset the whole store — call in a test `beforeEach`. */
export function resetPortalBerechtigungenMock(): void {
  STORE.clear();
  idSeq = 0;
}
